import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Clock3 } from "lucide-react";
import { api, resolveImage } from "@/lib/api";
import { Seo } from "@/context/SeoContext";

export default function ArticlePage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPost(null);
    api.get(`/blog/${slug}`).then(({ data }) => setPost(data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="article-not-found" className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="font-serif text-3xl font-semibold text-bark">Cet article s'est envolé…</h1>
        <Link to="/gazette" data-testid="back-to-gazette" className="mt-6 inline-flex items-center gap-2 text-amber-deep hover:text-bark">
          <ArrowLeft size={16} /> Retour à la Gazette
        </Link>
      </div>
    );
  }

  if (!post) {
    return <p data-testid="article-loading" className="py-24 text-center font-serif text-xl text-bark-soft">La ruche s'active…</p>;
  }

  const dateLabel = new Date(post.date).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <article data-testid="article-page" className="mx-auto max-w-3xl px-4 py-12 sm:px-8">
      <Seo
        title={`${post.title} | Pétales de Miel`}
        description={post.excerpt}
        image={post.image_url}
        path={`/gazette/${slug}`}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          image: resolveImage(post.image_url),
          datePublished: post.date,
          articleSection: post.tag,
          author: { "@type": "Organization", name: "Pétales de Miel" },
          publisher: { "@type": "Organization", name: "Pétales de Miel" },
        }}
      />
      <Link to="/gazette" data-testid="article-back" className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-bark-soft transition-colors hover:text-amber-deep">
        <ArrowLeft size={16} /> Retour à la Gazette
      </Link>

      <p className="eyebrow">{post.tag}</p>
      <h1 data-testid="article-title" className="mt-3 font-serif text-4xl font-semibold leading-tight tracking-tight text-bark sm:text-5xl">
        {post.title}
      </h1>
      <div className="mt-4 flex flex-wrap gap-5 text-sm text-stone-mute">
        <span className="flex items-center gap-1.5"><CalendarDays size={15} /> {dateLabel}</span>
        <span className="flex items-center gap-1.5"><Clock3 size={15} /> {post.read_time} de lecture</span>
      </div>

      <div className="mt-8 overflow-hidden rounded-[2rem] border border-amber-900/10 shadow-lg shadow-amber-900/5">
        <img src={post.image_url} alt={post.title} className="aspect-[16/9] w-full object-cover" />
      </div>

      <div className="mt-10 space-y-6">
        {post.content.map((paragraph, i) => (
          <p key={i} className={`text-base leading-relaxed text-bark-soft sm:text-lg ${i === 0 ? "drop-cap" : ""}`}>
            {paragraph}
          </p>
        ))}
      </div>

      <div className="mt-12 rounded-2xl border border-amber-800/15 bg-sand p-8 text-center">
        <p className="font-serif text-2xl font-medium text-bark">Envie de goûter le sujet de l'article ?</p>
        <Link to="/boutique" data-testid="article-cta-boutique" className="mt-4 inline-flex items-center gap-2 rounded-full bg-amber-deep px-7 py-3.5 font-medium text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95">
          Voir la boutique
        </Link>
      </div>
    </article>
  );
}
